<template>
  <div id="gap-backtest-strategy-config">
    <div class="container-fluid pt-4 px-4">
      <v-alert type="info" variant="tonal" density="comfortable" class="mb-4" rounded="lg">
        Configures each live-trading strategy separately from the main
        <router-link to="/gap-backtest/settings">Settings</router-link> page, which still owns every account-wide
        setting (capital, order mode, portfolio risk limits). A strategy's own <strong>Enabled</strong> switch here
        only gates signal generation/paper trading for that strategy -- real broker orders are still controlled
        exclusively by <strong>Order mode</strong> on the Settings page.
      </v-alert>

      <v-card flat rounded="lg" class="gap-backtest-card mb-4">
        <v-card-text class="px-5 py-4">
          <v-row align="center">
            <v-col cols="12" md="5">
              <v-select
                v-model="selectedType"
                :items="strategyTypeOptions"
                item-title="title" item-value="value"
                label="Strategy"
                variant="outlined" density="comfortable" hide-details
              />
            </v-col>
            <v-col cols="12" md="7" v-if="selected">
              <div class="d-flex align-center ga-3">
                <v-chip v-if="!selected.implemented" size="small" color="default" variant="tonal">Not yet implemented</v-chip>
                <template v-else>
                  <v-switch
                    v-model="form.enabled"
                    label="Enabled"
                    color="primary" density="comfortable" hide-details
                  />
                  <v-chip size="small" :color="form.enabled ? 'success' : 'default'" variant="tonal">
                    {{ form.enabled ? 'Signal generation ON' : 'Signal generation OFF' }}
                  </v-chip>
                </template>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- INDEX_MOMENTUM: info only, nothing editable -- see helpers/strategies/index.js -->
      <v-card v-if="selected && !selected.implemented" flat rounded="lg" class="gap-backtest-card mb-4">
        <v-card-text class="px-5 py-4">
          <v-alert type="info" variant="tonal" rounded="lg">
            Not yet implemented -- requires a live index price feed and a corporate-action/macro-event calendar,
            neither of which exists today. This entry exists so the schema and this page are ready to receive it
            once a real data source is chosen.
          </v-alert>
        </v-card-text>
      </v-card>

      <template v-else-if="selected">
        <v-card flat rounded="lg" class="gap-backtest-card mb-4">
          <v-card-title class="px-5 py-4">
            <span class="text-h6 font-weight-medium">{{ selected.strategyType }} configuration</span>
          </v-card-title>
          <v-card-text class="px-5 pb-5">
            <v-alert v-if="selectedType === 'SECTOR_MOMENTUM'" type="warning" variant="tonal" density="comfortable" class="mb-4" rounded="lg">
              No historical-edge validation yet -- every candidate this strategy finds is ranked purely on today's
              sector move, with no multi-year backtest behind it the way GAP_FADE has. Recommended paper/shadow-only
              until it has accumulated enough of its own trade history to build a real backtest from.
            </v-alert>

            <v-row dense>
              <v-col cols="12" sm="6" md="3">
                <v-text-field v-model.number="form.max_trades_per_day" type="number" label="Max trades/day" variant="outlined" density="comfortable" hide-details />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field v-model.number="form.stop_buffer_pct" type="number" label="Stop-loss buffer (%)" variant="outlined" density="comfortable" hide-details />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field v-model.number="form.min_stop_buffer_pct" type="number" label="Min stop-loss floor (%)" variant="outlined" density="comfortable" hide-details hint="Optional -- blank means no floor" persistent-hint />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-model="form.exit_strategy"
                  :items="[{ title: 'Single-stage', value: 'single_stage' }, { title: 'Multi-stage', value: 'multi_stage' }, { title: 'Trail runner', value: 'trail_runner' }]"
                  label="Exit strategy"
                  variant="outlined" density="comfortable" hide-details
                />
              </v-col>
            </v-row>
            <v-row dense class="mt-2">
              <v-col cols="12" sm="6" md="3">
                <v-switch v-model="form.reward_risk_gate_enabled" label="Reward:risk gate" color="primary" density="comfortable" hide-details />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field v-model.number="form.min_reward_risk_ratio" type="number" label="Min reward:risk ratio" variant="outlined" density="comfortable" hide-details :disabled="!form.reward_risk_gate_enabled" />
              </v-col>
              <v-col cols="12" sm="6" md="3" v-if="selectedType === 'GAP_FADE'">
                <v-text-field v-model.number="form.min_win_rate_threshold_pct" type="number" label="Min win-rate threshold (%)" variant="outlined" density="comfortable" hide-details />
              </v-col>
            </v-row>

            <v-divider class="my-4" />

            <template v-if="selectedType === 'BULLISH_MOMENTUM_PDH'">
              <v-row dense>
                <v-col cols="12" sm="6" md="3">
                  <v-text-field v-model.number="form.extra_config.min_avg_volume" type="number" label="Min 20-day Avg Volume" variant="outlined" density="comfortable" hide-details hint="Minimum 20-day volume for liquidity filter" persistent-hint />
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-text-field v-model.number="form.extra_config.min_rsi" type="number" label="Min Daily RSI(14)" variant="outlined" density="comfortable" hide-details hint="Minimum RSI to confirm bullish momentum" persistent-hint />
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-text-field v-model.number="form.extra_config.target_rr_multiple" type="number" label="Target reward:risk multiple" variant="outlined" density="comfortable" hide-details hint="Target distance = stop distance x this" persistent-hint />
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-text-field v-model.number="form.extra_config.max_breakout_extension_pct" type="number" step="0.1" label="Max breakout extension (%)" variant="outlined" density="comfortable" hide-details hint="Max % above PDH before skipping over-extension" persistent-hint />
                </v-col>
              </v-row>
              <v-row dense class="mt-2">
                <v-col cols="12" sm="6" md="3">
                  <v-text-field v-model.number="form.extra_config.min_price" type="number" label="Min stock price (₹)" variant="outlined" density="comfortable" hide-details />
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-text-field v-model.number="form.extra_config.max_price" type="number" label="Max stock price (₹)" variant="outlined" density="comfortable" hide-details />
                </v-col>
              </v-row>
            </template>
            <template v-else-if="selectedType === 'GAP_FADE'">
              <v-row dense>
                <v-col cols="12" sm="6" md="3">
                  <v-text-field v-model.number="form.extra_config.max_gap_pct" type="number" label="Max gap (%) sanity cap" variant="outlined" density="comfortable" hide-details hint="A gap bigger than this is treated as a bad tick" persistent-hint />
                </v-col>
              </v-row>
            </template>
            <template v-else-if="selectedType === 'SECTOR_MOMENTUM'">
              <v-row dense>
                <v-col cols="12" sm="6" md="3">
                  <v-text-field v-model.number="form.extra_config.min_sector_move_pct" type="number" label="Min sector move (%)" variant="outlined" density="comfortable" hide-details hint="A sector must move at least this much to qualify" persistent-hint />
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-text-field v-model.number="form.extra_config.min_sector_stock_count" type="number" label="Min sector sample size" variant="outlined" density="comfortable" hide-details hint="Fewer onboarded stocks than this and the sector never qualifies" persistent-hint />
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-text-field v-model.number="form.extra_config.target_rr_multiple" type="number" label="Target reward:risk multiple" variant="outlined" density="comfortable" hide-details hint="Target distance = stop distance x this" persistent-hint />
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-text-field v-model.number="form.extra_config.sector_perf_refresh_minutes" type="number" label="Sector data refresh (min)" variant="outlined" density="comfortable" hide-details />
                </v-col>
              </v-row>
            </template>

            <div class="d-flex align-center ga-3 mt-4">
              <v-btn color="primary" variant="tonal" :loading="saving" @click="save">Save</v-btn>
              <span v-if="saveMessage" class="text-caption" :class="saveError ? 'text-error' : 'text-success'">{{ saveMessage }}</span>
            </div>
          </v-card-text>
        </v-card>

        <!-- Universe funnel -- SECTOR_MOMENTUM only, see helpers/sector-performance.js -->
        <v-card v-if="selectedType === 'SECTOR_MOMENTUM'" flat rounded="lg" class="gap-backtest-card mb-4">
          <v-card-title class="d-flex justify-space-between align-center flex-wrap ga-3 px-5 py-4">
            <span class="text-h6 font-weight-medium">Universe funnel</span>
            <v-btn variant="text" size="small" :loading="loadingFunnel" @click="loadFunnel">
              <font-awesome-icon icon="fa-solid fa-rotate-right" class="mr-1" /> Refresh
            </v-btn>
          </v-card-title>
          <v-card-text class="px-5 pb-5">
            <div v-if="!funnel" class="text-caption text-medium-emphasis">Not loaded yet.</div>
            <template v-else>
              <v-row dense class="mb-2">
                <v-col cols="6" sm="3" v-for="stat in funnelStats" :key="stat.key">
                  <div class="text-caption text-medium-emphasis">{{ stat.label }}</div>
                  <div class="text-h6">{{ funnel[stat.key] ?? 0 }}</div>
                </v-col>
              </v-row>
              <div class="text-caption text-medium-emphasis mb-3">
                This is the stage-by-stage narrowing from the full onboarded universe down to codes actually eligible
                to trade -- a coherent decline at each stage means the strategy/thresholds are doing real filtering;
                an unexplained cliff at one stage is worth investigating (e.g. a threshold set too strict).
              </div>
              <v-data-table
                :headers="[
                  { title: 'Sector', key: 'sectorLabel' },
                  { title: 'Stocks', key: 'validQuoteCount' },
                  { title: 'Avg move %', key: 'avgMovePct' },
                  { title: 'Median move %', key: 'medianMovePct' },
                  { title: 'Qualifies (sample)', key: 'qualifiesOnSampleSize' },
                  { title: 'Qualifies (move)', key: 'qualifiesOnMove' },
                ]"
                :items="funnel.perSector || []"
                item-value="sectorLabel"
                fixed-header
                :items-per-page="-1"
                hide-default-footer
                class="custom-data-table"
                density="compact"
              >
                <template v-slot:[`item.qualifiesOnSampleSize`]="{ item }">
                  <font-awesome-icon v-if="item.qualifiesOnSampleSize" icon="fa-solid fa-check" class="text-success" />
                  <font-awesome-icon v-else icon="fa-solid fa-xmark" class="text-medium-emphasis" />
                </template>
                <template v-slot:[`item.qualifiesOnMove`]="{ item }">
                  <font-awesome-icon v-if="item.qualifiesOnMove" icon="fa-solid fa-check" class="text-success" />
                  <font-awesome-icon v-else icon="fa-solid fa-xmark" class="text-medium-emphasis" />
                </template>
              </v-data-table>
            </template>
          </v-card-text>
        </v-card>

        <v-card flat rounded="lg" class="gap-backtest-card">
          <v-card-title class="px-5 py-4">
            <span class="text-h6 font-weight-medium">Watchlist codes on {{ selectedType }}</span>
          </v-card-title>
          <v-card-text class="px-5 pb-3 text-caption text-medium-emphasis">
            Only codes assigned here actually reach order placement under this strategy -- see the info banner above.
          </v-card-text>
          <v-data-table
            :headers="[{ title: 'Code', key: 'code' }, { title: 'Reassign to', key: 'reassign', sortable: false }]"
            :items="selected.watchlistCodes || []"
            item-value="code"
            fixed-header
            :items-per-page="-1"
            hide-default-footer
            class="custom-data-table"
          >
            <template v-slot:[`item.reassign`]="{ item }">
              <v-select
                :model-value="selectedType"
                :items="implementedStrategyTypes"
                density="compact" variant="outlined" hide-details style="max-width: 220px"
                @update:model-value="(newType) => reassign(item.code, newType)"
              />
            </template>
            <template v-slot:no-data>
              <div class="py-6 text-center text-medium-emphasis">No watchlist codes assigned to this strategy yet.</div>
            </template>
          </v-data-table>
        </v-card>
      </template>
    </div>
  </div>
</template>

<script>
import { fetchWrapper } from '@/helpers/fetch-wrapper';

export default {
  name: 'GapBacktestStrategyConfig-page',
  data() {
    return {
      strategies: [], // [{ strategyType, implemented, settings, watchlistCodes }]
      selectedType: 'BULLISH_MOMENTUM_PDH',
      form: { enabled: true, max_trades_per_day: 2, stop_buffer_pct: 0.6, min_stop_buffer_pct: null, reward_risk_gate_enabled: false, min_reward_risk_ratio: 1.5, exit_strategy: 'trail_runner', min_win_rate_threshold_pct: null, extra_config: {} },
      saving: false,
      saveMessage: '',
      saveError: false,
      funnel: null,
      loadingFunnel: false,
      funnelStats: [
        { key: 'totalUniverseStocks', label: 'Universe' },
        { key: 'sectorTaggedStocks', label: 'Sector-tagged' },
        { key: 'validQuoteStocks', label: 'Valid quote' },
        { key: 'validPrevCloseStocks', label: 'Valid prev close' },
        { key: 'sectorsEvaluated', label: 'Sectors' },
        { key: 'sectorsMeetingSampleSize', label: 'Sectors: sample OK' },
        { key: 'sectorsMeetingMoveThreshold', label: 'Sectors: move OK' },
        { key: 'directionAgreeingStocks', label: 'Discovered candidates' },
        { key: 'watchlistEligibleStocks', label: 'Watchlist-eligible' },
      ],
    };
  },
  computed: {
    strategyTypeOptions() {
      return this.strategies.map((s) => ({
        title: s.implemented ? s.strategyType : `${s.strategyType} (not yet implemented)`,
        value: s.strategyType,
      }));
    },
    implementedStrategyTypes() {
      return this.strategies.filter((s) => s.implemented).map((s) => s.strategyType);
    },
    selected() {
      return this.strategies.find((s) => s.strategyType === this.selectedType) || null;
    },
  },
  watch: {
    selectedType() {
      this.applySelectedToForm();
      this.saveMessage = '';
      if (this.selectedType === 'SECTOR_MOMENTUM' && !this.funnel) this.loadFunnel();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      return fetchWrapper.get('api/strategy-settings').then((res) => {
        this.strategies = res?.data || [];
        this.applySelectedToForm();
      }).catch((err) => {
        console.error('Failed to load strategy settings:', err);
      });
    },
    applySelectedToForm() {
      if (!this.selected || !this.selected.implemented || !this.selected.settings) return;
      const s = this.selected.settings;
      this.form = {
        enabled: Boolean(s.enabled),
        max_trades_per_day: s.max_trades_per_day,
        stop_buffer_pct: s.stop_buffer_pct,
        min_stop_buffer_pct: s.min_stop_buffer_pct,
        reward_risk_gate_enabled: Boolean(s.reward_risk_gate_enabled),
        min_reward_risk_ratio: s.min_reward_risk_ratio,
        exit_strategy: s.exit_strategy,
        min_win_rate_threshold_pct: s.min_win_rate_threshold_pct,
        extra_config: { ...(s.extra_config || {}) },
      };
    },
    save() {
      if (!this.selected || !this.selected.implemented) return;
      this.saving = true;
      this.saveMessage = '';
      fetchWrapper.put(`api/strategy-settings/${this.selectedType}`, this.form).then(() => {
        this.saveMessage = 'Saved.';
        this.saveError = false;
        return this.load();
      }).catch((err) => {
        this.saveMessage = typeof err === 'string' ? err : 'Failed to save.';
        this.saveError = true;
      }).finally(() => {
        this.saving = false;
      });
    },
    reassign(code, newType) {
      if (newType === this.selectedType) return;
      fetchWrapper.put(`api/strategy-settings/watchlist/${encodeURIComponent(code)}`, { strategyType: newType }).then(() => {
        this.load();
      }).catch((err) => {
        console.error('Failed to reassign watchlist strategy:', err);
      });
    },
    loadFunnel() {
      this.loadingFunnel = true;
      fetchWrapper.get('api/strategy-settings/sector-momentum/funnel').then((res) => {
        this.funnel = res?.data || null;
        this.loadingFunnel = false;
      }).catch((err) => {
        console.error('Failed to load the sector momentum funnel:', err);
        this.loadingFunnel = false;
      });
    },
  },
};
</script>
